<main class="mfa-verification" role="main">
  <div class="verification-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="logo-container">
        <img src="../../../assets/report_v2/inspera_originality.svg" alt="Inspera Originality Logo" class="logo">
      </div>

      <div class="security-badge">
        <i class="fas fa-shield-alt"></i>
        <span>Secure Verification</span>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <div class="verification-header">
        <h1>Two-Factor Authentication</h1>
        <p class="subtitle">Please verify your identity to complete the login process</p>
        <div class="user-info">
          <i class="fas fa-user-circle"></i>
          <span>{{ userEmail }}</span>
        </div>
      </div>

      <!-- Method Selection Tabs -->
      <div class="method-tabs" *ngIf="mfaMethod === 'BOTH'">
        <button class="method-tab" [class.active]="selectedMethod === 'EMAIL'" (click)="switchMethod('EMAIL')">
          <i class="fas fa-envelope"></i>
          <span>{{ 'new_entries.email_verification' | translate }}</span>
        </button>

        <button class="method-tab" [class.active]="selectedMethod === 'TOTP'" (click)="switchMethod('TOTP')">
          <i class="fas fa-mobile-alt"></i>
          <span>{{ 'new_entries.authenticator_app' | translate }}</span>
        </button>
      </div>

      <!-- Verification Form -->
      <div class="verification-form-container">
        <!-- Email Method -->
        <div class="verification-method" *ngIf="selectedMethod === 'EMAIL'">
          <div class="method-header">
            <div class="method-icon email-icon">
              <i class="fas fa-envelope"></i>
            </div>
            <div class="method-info">
              <h3>{{ 'new_entries.email_verification' | translate }}</h3>
              <p>Click the button below to send a 6-digit verification code to your email address.</p>
            </div>
          </div>

          <!-- Send Code Button for Email -->
          <div class="send-code-section" *ngIf="!isCodeSent">
            <button type="button" class="send-code-button" (click)="sendEmailCode()"
              [disabled]="isLoading || resendTimer > 0" [class.loading]="isLoading">
              <span *ngIf="!isLoading">
                <i class="fas fa-paper-plane"></i>
                Send Verification Code
              </span>
              <span *ngIf="isLoading" class="loading-content">
                <i class="fas fa-spinner fa-spin"></i>
                Sending...
              </span>
            </button>
          </div>

          <!-- Code Sent Status -->
          <div class="status-message" *ngIf="isCodeSent && !isLoading">
            <i class="fas fa-check-circle"></i>
            <span>Code sent successfully! Check your email and enter the code below.</span>
          </div>
        </div>

        <!-- TOTP Method -->
        <div class="verification-method" *ngIf="selectedMethod === 'TOTP'">
          <div class="method-header">
            <div class="method-icon totp-icon">
              <i class="fas fa-mobile-alt"></i>
            </div>
            <div class="method-info">
              <h3>{{ 'new_entries.authenticator_app' | translate }}</h3>
              <p>Enter the 6-digit code from your authenticator app (Google Authenticator, Authy, etc.).</p>
            </div>
          </div>

          <div class="status-message info">
            <i class="fas fa-info-circle"></i>
            <span>Open your authenticator app and enter the current 6-digit code below.</span>
          </div>
        </div>

        <!-- Backup Code Method -->
        <div class="verification-method" *ngIf="selectedMethod === 'BACKUP'">
          <div class="method-header">
            <div class="method-icon backup-icon">
              <i class="fas fa-key"></i>
            </div>
            <div class="method-info">
              <h3>Backup Recovery Code</h3>
              <p>Enter one of your backup recovery codes. Format: XXXXX-XXXXX</p>
            </div>
          </div>

          <div class="status-message info">
            <i class="fas fa-info-circle"></i>
            <span>Each backup code can only be used once. Enter the code in XXXXX-XXXXX format.</span>
          </div>
        </div>

        <!-- Code Input Form -->
        <form [formGroup]="verificationForm" (ngSubmit)="verifyCode()" class="code-form"
          *ngIf="(selectedMethod === 'EMAIL' && isCodeSent) || selectedMethod === 'TOTP' || selectedMethod === 'BACKUP'">
          <div class="code-input-container">
            <div class="input-wrapper">
              <input type="text" formControlName="code"
                [placeholder]="selectedMethod === 'BACKUP' ? 'XXXXX-XXXXX' : '000000'" (input)="onCodeInput($event)"
                [disabled]="isLoading || isLockedOut" class="code-input" autocomplete="off"
                [maxlength]="selectedMethod === 'BACKUP' ? 11 : 6">
              <div class="input-icon">
                <i class="fas" [ngClass]="{
                     'fa-envelope': selectedMethod === 'EMAIL',
                     'fa-mobile-alt': selectedMethod === 'TOTP',
                     'fa-key': selectedMethod === 'BACKUP'
                   }">
                </i>
              </div>
            </div>

            <div class="validation-error" *ngIf="codeControl?.touched && codeControl?.invalid">
              <i class="fas fa-exclamation-circle"></i>
              <span *ngIf="selectedMethod === 'BACKUP'">Please enter a valid backup code format: XXXXX-XXXXX</span>
              <span *ngIf="selectedMethod !== 'BACKUP'">Please enter a valid 6-digit code</span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button type="submit" class="verify-button" [disabled]="verificationForm.invalid || isLoading"
              [class.loading]="isLoading">
              <span *ngIf="!isLoading">
                <i class="fas fa-shield-check"></i>
                Verify & Continue
              </span>
              <span *ngIf="isLoading" class="loading-content">
                <i class="fas fa-spinner fa-spin"></i>
                Verifying...
              </span>
            </button>

            <!-- Resend Button for Email -->
            <button type="button" class="resend-button" *ngIf="selectedMethod === 'EMAIL'" (click)="sendEmailCode()"
              [disabled]="resendTimer > 0 || isLoading">
              <i class="fas fa-redo"></i>
              <span *ngIf="resendTimer === 0">Resend Code</span>
              <span *ngIf="resendTimer > 0">Resend in {{ resendTimer }}s</span>
            </button>
          </div>
        </form>

        <!-- Alternative Methods -->
        <div class="alternative-methods">
          <!-- Backup Codes Option -->
          <button class="alt-method-link" *ngIf="selectedMethod !== 'BACKUP'" (click)="switchMethod('BACKUP')">
            <i class="fas fa-key"></i>
            Use a backup recovery code
          </button>

          <!-- Back to other methods -->
          <button class="alt-method-link" *ngIf="selectedMethod === 'BACKUP' && isMethodAvailable('EMAIL')"
            (click)="switchMethod('EMAIL')">
            <i class="fas fa-envelope"></i>
            Use email verification instead
          </button>

          <button class="alt-method-link" *ngIf="selectedMethod === 'BACKUP' && isMethodAvailable('TOTP')"
            (click)="switchMethod('TOTP')">
            <i class="fas fa-mobile-alt"></i>
            Use authenticator app instead
          </button>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer-section">
      <button class="back-to-login" (click)="goBackToLogin()">
        <i class="fas fa-arrow-left"></i>
        Back to Login
      </button>

      <div class="help-text">
        <p>Having trouble? Contact your system administrator for assistance.</p>
      </div>
    </div>
  </div>

  <!-- Background Decoration -->
  <div class="bg-decoration">
    <div class="floating-shape shape-1"></div>
    <div class="floating-shape shape-2"></div>
    <div class="floating-shape shape-3"></div>
  </div>
</main>